import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { UILoader } from '@futrli/ui-components';
import { workspaceByIdSelector } from '@futrli/shared-services';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';

import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { LIABILITIES_CATEGORIES, StoreState, VIEW_TYPES } from '../../../../core/types';
import { CATEGORY_NAME_INDEXES, REPORT_SECTION } from '../../../../core/constants';
import { UserGridSettingsContext } from '../../reports/user-grid-settings/user-grid-settings-context';
import { ProfitLossPredictionsPageWrapper } from '../profit-loss-predictions-page-wrapper';
import { getConsolidatedGridInfo } from '../util-fns/consolidation-util-fns';
import { useConsolidationDataContext } from '../../../../predict-management/context/consolidation-data-context';
import { ForecastUpdating } from '../../components/forecast-updating';

export const ConsolidationLiabilities = () => {
  const intl = useIntl();
  const { workspaceId: tempWorkspaceId } = useParams();

  const workspaceId = tempWorkspaceId || '';

  const {
    category,
    consolidatedForecastReport,
    forecast,
    isLoading,
    periodType,
    totalCashPredictRow,
    viewType
  } = usePredictionsInfoContext();

  const workspace = useSelector((state: StoreState) => workspaceByIdSelector(state.normalised, workspaceId));

  const { nodeStaticIds } = useLocalisedConstantsContext();

  const { consolidation } = useConsolidationDataContext();

  const { expandedRows, rowHeights, getShowZeroValueAccounts } = useContext(UserGridSettingsContext);

  const categoryName =
    category === LIABILITIES_CATEGORIES.NON_CURRENT_LIABILITIES
      ? CATEGORY_NAME_INDEXES.non_current_liabilities
      : CATEGORY_NAME_INDEXES.current_liabilities;

  const viewOptions = useMemo(() => {
    return [
      {
        name: intl.formatMessage({
          id: 'predict.accruals_bs_view',
          defaultMessage: 'Accruals (BS) view'
        }),
        value: VIEW_TYPES.ACCRUALS_BS
      },
      {
        name: intl.formatMessage({
          id: 'predict.cash_flow_view',
          defaultMessage: 'Cash flow view'
        }),
        value: VIEW_TYPES.CASH_FLOW
      }
    ];
  }, [intl]);

  const categoryOptions = useMemo(() => {
    return [
      {
        name: intl.formatMessage({
          id: 'financial_fields.category.current_liabilities',
          defaultMessage: 'Current Liabilities'
        }),
        value: LIABILITIES_CATEGORIES.CURRENT_LIABILITIES
      },
      {
        name: intl.formatMessage({
          id: 'financial_fields.category.non_current_liabilities',
          defaultMessage: 'Non Current Liabilities'
        }),
        value: LIABILITIES_CATEGORIES.NON_CURRENT_LIABILITIES
      }
    ];
  }, [intl]);

  const { dataGridRows, totalRow } = useMemo(() => {
    return getConsolidatedGridInfo(
      intl,
      nodeStaticIds,
      viewType === VIEW_TYPES.CASH_FLOW ? REPORT_SECTION.CashFlow : REPORT_SECTION.BalanceSheet,
      categoryName,
      workspace,
      expandedRows,
      periodType,
      consolidatedForecastReport,
      consolidation,
      getShowZeroValueAccounts(forecast?.id || '')
    );
  }, [
    intl,
    nodeStaticIds,
    viewType,
    categoryName,
    workspace,
    expandedRows,
    periodType,
    consolidatedForecastReport,
    consolidation,
    getShowZeroValueAccounts,
    forecast?.id
  ]);

  if (!consolidatedForecastReport && isLoading) {
    return <UILoader isFull />;
  }

  return (
    <>
      <ProfitLossPredictionsPageWrapper
        reportColumns={
          viewType === VIEW_TYPES.CASH_FLOW
            ? consolidatedForecastReport?.data.cash_flow_columns
            : consolidatedForecastReport?.data.columns
        }
        rowHeights={rowHeights}
        viewOptions={viewOptions}
        accountRows={dataGridRows}
        totalRow={totalRow}
        category={categoryName}
        categoryOptions={categoryOptions}
        totalCashPredictRow={totalCashPredictRow}
      />
      <ForecastUpdating show={!!isLoading} />
    </>
  );
};
